import { useMemo } from 'react';
import { useLoaderData } from 'react-router';
import { useReducedMotion } from 'framer-motion';
import { useHydrated } from 'remix-utils';

import type { loader } from '~/routes/_app.product.$urlSegment';
import {
    useIslandSection,
    useMagicIslandController,
    type IslandMode,
} from '~/ui-rocco/pages/detail-shared';
import { useEnquiryOwnsIsland } from '../../hooks/use-enquiry-owns-island';
import { useSimilarProducts } from '../use-similar-products';
import { SimilarProductsGrid } from '../SimilarProductsGrid';

/**
 * (NN) Similar Products — inside the spec accordion on desktop (default open),
 * so the "(NN)" number continues the accordion sequence automatically. The
 * standalone mobile section (`SimilarProductsSection`) renders the same grid.
 * Renders nothing until the first page of category products arrives.
 *
 * Desktop hands the Magic Island the "Load More" control: while more pages
 * remain it's a CTA that appends the next page in place; once exhausted it
 * morphs into a "View all in {category}" link out to the directory. The inline
 * pill then hides — except under reduced motion (the island animates), before
 * hydration, or when the island flag is off, where the inline pill stays so
 * there is always a control. Mobile keeps the inline pill (it never reaches
 * this component).
 */
export const SimilarProductsRowContent = () => {
    const similar = useSimilarProducts();
    const { primaryCategory } = useLoaderData<typeof loader>();
    const islandMounted = useMagicIslandController() !== null;
    const reduce = useReducedMotion() ?? false;
    const hydrated = useHydrated();

    const islandActive = islandMounted && !reduce;
    const categoryLink = primaryCategory?.Link ?? '';
    // Once exhausted with nowhere to send the user, drop the island rather than
    // show a link to nothing.
    const islandEnabled =
        islandActive &&
        similar.hasItems &&
        (similar.hasMore || categoryLink !== '');

    const categoryName = primaryCategory?.PrettyTitle ?? 'this category';
    const islandMode = useMemo<IslandMode>(() => {
        if (similar.hasMore) {
            return {
                kind: 'cta',
                label: similar.isLoading
                    ? 'Loading…'
                    : `See more products in ${categoryName}`,
                onClick: similar.loadMore,
                disabled: similar.isLoading,
            };
        }
        return {
            kind: 'link',
            label: `See more products in ${categoryName}`,
            to: categoryLink,
        };
    }, [
        similar.hasMore,
        similar.isLoading,
        similar.loadMore,
        categoryName,
        categoryLink,
    ]);

    // Past half-scroll the island belongs to the enquiry fallback (the page's
    // QDD action) — this nav claim only runs through the first half.
    const enquiryOwnsIsland = useEnquiryOwnsIsland();
    const islandRef = useIslandSection<HTMLDivElement>(islandMode, {
        enabled: islandEnabled && !enquiryOwnsIsland,
    });

    if (!similar.hasItems) return null;

    return (
        <div ref={islandRef} className="pb-[1.8rem] max-md:pb-4">
            <SimilarProductsGrid
                // Browse tile scale inside the accordion, matching the range
                // and supplier rows.
                tileTextClassName="md:text-[1rem] md:leading-[1.3]"
                {...similar}
                // The inline pill hides only while the island actually carries
                // the See-more CTA — once the enquiry fallback owns the island
                // past half-scroll, the pill returns as the only pagination.
                hideLoadMore={islandActive && hydrated && !enquiryOwnsIsland}
            />
        </div>
    );
};
